import { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Checkbox, Group, Loader, Modal, Stack, Text, Tooltip } from '@mantine/core';

import { useMonitoringEnrolment } from '@/hooks/useMonitoringEnrolment';
import { monitoringComponentLabel } from '@/types/monitoring';

interface AddToMonitoringModalProps {
  opened: boolean;
  onClose: () => void;
  reportId: string;
  /** Called once the subscription exists, so the caller can confirm or refresh. */
  onEnrolled?: () => void;
}

/**
 * Enrol a finished report's subjects into ongoing monitoring.
 *
 * Subjects and components are both explicit choices with nothing pre-ticked.
 * That is deliberate: monitoring bills per subject and runs indefinitely, so
 * defaulting to "all" would make the expensive option the one you get by not
 * reading the dialog.
 *
 * What can be offered comes from the server, which derives it from what the
 * report actually ran — a client cannot monitor PEPs off a report that never
 * screened them, because there would be no baseline behind the subscription.
 *
 * A subject already enrolled is shown with its subscription's status. One
 * still pending — Castellum has not accepted it — can be retried from here.
 */
export function AddToMonitoringModal({
  opened,
  onClose,
  reportId,
  onEnrolled,
}: Readonly<AddToMonitoringModalProps>) {
  const { options, isLoading, isSubmitting, error, enrol, register } = useMonitoringEnrolment(
    reportId,
    opened
  );

  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryErrors, setRetryErrors] = useState<Record<string, string>>({});

  // Cleared on close so reopening never inherits a previous, possibly stale
  // selection — the report's offer can change between openings.
  useEffect(() => {
    if (!opened) {
      setSelectedSubjects([]);
      setSelectedComponents([]);
      setSubmitError(null);
      setRegistrationError(null);
      setRetryErrors({});
    }
  }, [opened]);

  const canSubmit = useMemo(
    () => selectedSubjects.length > 0 && selectedComponents.length > 0 && !isSubmitting,
    [selectedSubjects, selectedComponents, isSubmitting]
  );

  const handleEnrol = async () => {
    setSubmitError(null);
    setRegistrationError(null);
    try {
      const result = await enrol(selectedSubjects, selectedComponents);
      onEnrolled?.();
      if (result.registrationError) {
        // The enrolment exists, but closing would read as success, and this is
        // the only place the analyst learns Castellum refused it. The enrolled
        // subjects now show as pending, with the retry beside them.
        setSelectedSubjects([]);
        setSelectedComponents([]);
        setRegistrationError(result.registrationError);
        return;
      }
      onClose();
    } catch (err) {
      // Kept open on failure: the analyst's selection is worth more than a
      // tidy close, and a 400 here means the selection itself needs changing.
      setSubmitError(err instanceof Error ? err.message : 'Could not add to monitoring');
    }
  };

  const handleRetry = async (monitoredSubjectId: string) => {
    setRetrying(monitoredSubjectId);
    setRetryErrors((current) => {
      const next = { ...current };
      delete next[monitoredSubjectId];
      return next;
    });
    try {
      const outcome = await register(monitoredSubjectId);
      if (!outcome.registered) {
        setRetryErrors((current) => ({
          ...current,
          [monitoredSubjectId]: `Castellum still refused it: ${outcome.registrationError ?? 'no reason given'}`,
        }));
      }
    } catch (err) {
      setRetryErrors((current) => ({
        ...current,
        [monitoredSubjectId]: err instanceof Error ? err.message : 'Could not retry registration',
      }));
    } finally {
      setRetrying(null);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add to monitoring" size="md">
      <Stack>
        {isLoading && <Loader size="sm" />}

        {error && !isLoading && (
          <Alert color="red" title="Could not load monitoring options">
            {error.message}
          </Alert>
        )}

        {options && !options.available && (
          <Alert color="gray" title="Not available for this report">
            {options.blockedReason}
          </Alert>
        )}

        {options?.available && (
          <>
            <Text size="sm" c="dimmed">
              Monitoring watches these subjects continuously and alerts on new or changed findings.
              It runs until cancelled and is billed per subject.
            </Text>

            <Stack gap="xs">
              <Text fw={600} size="sm">
                Subjects
              </Text>
              {options.subjects.map((subject) => {
                const monitoredId = subject.monitoredSubjectId;
                return (
                  <Stack key={subject.subjectId} gap={2}>
                    <Group gap="xs" wrap="nowrap">
                      {/* Shown disabled rather than omitted: a subject missing
                          from the list reads as "this report had no such
                          subject", and the analyst needs to know it is enrolled. */}
                      <Checkbox
                        checked={selectedSubjects.includes(subject.subjectId)}
                        disabled={subject.monitoredStatus !== null}
                        onChange={(event) => {
                          // Read before the updater runs: React defers it, by
                          // which point `currentTarget` is null.
                          const { checked } = event.currentTarget;
                          setSelectedSubjects((current) =>
                            checked
                              ? [...current, subject.subjectId]
                              : current.filter((id) => id !== subject.subjectId)
                          );
                        }}
                        label={subject.name}
                      />
                      {subject.monitoredStatus === 'pending' && (
                        <>
                          <Tooltip label="Enrolled, but Castellum has not accepted it yet. Retried automatically every few minutes.">
                            <Text size="xs" c="orange">
                              registration pending
                            </Text>
                          </Tooltip>
                          {monitoredId && (
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              loading={retrying === monitoredId}
                              disabled={retrying !== null && retrying !== monitoredId}
                              onClick={() => handleRetry(monitoredId)}
                            >
                              Retry now
                            </Button>
                          )}
                        </>
                      )}
                      {subject.monitoredStatus === 'suspended' && (
                        <Text size="xs" c="dimmed">
                          monitoring suspended
                        </Text>
                      )}
                      {subject.monitoredStatus === 'active' && (
                        <Text size="xs" c="dimmed">
                          already monitored
                        </Text>
                      )}
                      {/* Surfaced rather than hidden: registering a name nobody
                          verified is worth catching before billing starts. */}
                      {subject.monitoredStatus === null &&
                        subject.nameSource !== 'selected_candidate' && (
                          <Tooltip label="This name was not confirmed during identification">
                            <Text size="xs" c="orange">
                              unverified name
                            </Text>
                          </Tooltip>
                        )}
                    </Group>
                    {monitoredId && retryErrors[monitoredId] && (
                      <Text size="xs" c="red">
                        {retryErrors[monitoredId]}
                      </Text>
                    )}
                  </Stack>
                );
              })}
            </Stack>

            <Stack gap="xs">
              <Text fw={600} size="sm">
                Checks
              </Text>
              <Text size="xs" c="dimmed">
                Only checks this report actually ran can be monitored.
              </Text>
              {options.componentSlugs.map((slug) => (
                <Checkbox
                  key={slug}
                  checked={selectedComponents.includes(slug)}
                  onChange={(event) => {
                    const { checked } = event.currentTarget;
                    setSelectedComponents((current) =>
                      checked ? [...current, slug] : current.filter((value) => value !== slug)
                    );
                  }}
                  label={monitoringComponentLabel(slug)}
                />
              ))}
            </Stack>

            {submitError && (
              <Alert color="red" title="Could not add to monitoring">
                {submitError}
              </Alert>
            )}

            {registrationError && (
              <Alert color="orange" title="Enrolled, but registration failed">
                <Stack gap={4}>
                  <Text size="sm">{registrationError}</Text>
                  <Text size="sm">
                    The subjects are saved as pending and retried automatically every few minutes.
                    Once the cause is fixed you can retry them above.
                  </Text>
                </Stack>
              </Alert>
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            {registrationError ? 'Close' : 'Cancel'}
          </Button>
          {options?.available && (
            <Button onClick={handleEnrol} disabled={!canSubmit} loading={isSubmitting}>
              Add to monitoring
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
